// src/components/invoiceFormats/LetterheadFormat.jsx
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { format } from "date-fns";
import { httpService } from "../../services/httpService";
import { useProfile } from "../../context/ProfileContext";

/* ================================================================
   HELPERS
   ================================================================ */
const formatAddress = (addr) => {
  if (!addr) return "";
  if (typeof addr === "string") return addr.trim();
  if (Array.isArray(addr))
    return addr.map(formatAddress).filter(Boolean).join(", ");

  if (typeof addr === "object") {
    if (
      typeof addr.full_address === "string" &&
      addr.full_address.trim() !== ""
    )
      return addr.full_address;
    if (typeof addr.address === "string" && addr.address.trim() !== "")
      return addr.address;
    if (addr.officeAddress || addr.homeAddress) {
      const off = formatAddress(addr.officeAddress);
      const home = formatAddress(addr.homeAddress);
      return [off, home].filter(Boolean).join(", ");
    }
    if (
      addr.buildingName ||
      addr.locality ||
      addr.city ||
      addr.state ||
      addr.pincode
    ) {
      const parts = [
        addr.buildingName,
        addr.floor,
        addr.locality,
        addr.city,
        addr.state,
        addr.pincode,
      ]
        .filter(Boolean)
        .map((s) => (typeof s === "string" ? s.trim() : s));
      return parts.join(", ");
    }
    if (typeof addr.address === "object") return formatAddress(addr.address);
    return Object.values(addr)
      .filter((v) => typeof v === "string" && v.trim() !== "")
      .join(", ");
  }
  return String(addr);
};

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};
const fmtMoney = (v) => `₹${num(v).toFixed(2)}`;
const truncate = (str, n = 25) => {
  const s = String(str || "");
  return s.length > n ? s.slice(0, n) + "..." : s;
};
const fmtDate = (d) => {
  try {
    return d ? format(new Date(d), "dd/MM/yyyy") : "—";
  } catch {
    return "—";
  }
};

const numberToWords = (numVal) => {
  const value = num(numVal);
  if (!value) return "Zero Rupees Only";
  const a = [
    "",
    "One",
    "Two",
    "Three",
    "Four",
    "Five",
    "Six",
    "Seven",
    "Eight",
    "Nine",
    "Ten",
    "Eleven",
    "Twelve",
    "Thirteen",
    "Fourteen",
    "Fifteen",
    "Sixteen",
    "Seventeen",
    "Eighteen",
    "Nineteen",
  ];
  const b = [
    "",
    "",
    "Twenty",
    "Thirty",
    "Forty",
    "Fifty",
    "Sixty",
    "Seventy",
    "Eighty",
    "Ninety",
  ];
  const w = (n, s) => {
    let str = n > 19 ? b[Math.floor(n / 10)] + " " + a[n % 10] : a[n];
    if (n) str += " " + s;
    return str;
  };
  const intPart = Math.floor(value);
  const decPart = Math.round((value - intPart) * 100);
  let out = "";
  out += w(Math.floor(intPart / 10000000), "Crore ");
  out += w(Math.floor((intPart / 100000) % 100), "Lakh ");
  out += w(Math.floor((intPart / 1000) % 100), "Thousand ");
  out += w(Math.floor((intPart / 100) % 10), "Hundred ");
  if (intPart > 100 && intPart % 100) out += "and ";
  out += w(Math.floor(intPart % 100), "");
  let final = "Rupees " + out.trim();
  if (decPart > 0) final += ` and ${w(decPart, "")} Paise`;
  return final + " Only";
};

/* ================================================================
   NORMALIZE SNAPSHOT
   ================================================================ */
const classifyChild = (it) => {
  const src = String(it.source || "").toLowerCase();
  const st = String(it.service_type || "").toLowerCase();
  if (src === "nested_product" || st === "product" || it.is_product)
    return "product";
  if (src === "nested_addon" || st === "addon" || it.is_addon) return "addon";
  if (src === "other_charge" || st === "other_charge" || it.is_other_charge)
    return "other";
  return "product";
};

const normalizeSnapshot = (snapRaw) => {
  const snap = snapRaw?.data ?? snapRaw ?? {};
  const vendor = snap.vendor ?? {};
  const billTo = snap.bill_to ?? {};
  const summary = snap.summary ?? {};
  const invoiceAmount = snap.invoice_amount ?? {};

  const vendorAddress = formatAddress(vendor.address);
  const rawItems = Array.isArray(snap.items) ? snap.items : [];

  const parentByRef = new Map();
  const parents = [];

  for (const it of rawItems) {
    if (it.is_child_row) continue;
    const key = String(it.ref_id ?? it._id ?? parents.length);
    const parent = {
      _id: it._id ?? key,
      _key: key,
      service_name: it.description ?? it.service_name ?? "Item",
      item_type: it.service_type ?? "",
      hsn_code: it.hsn_code ?? "",
      expiry: it.expiry ?? "",
      batch_no: it.batch_number ?? it.batch_no ?? "",
      quantity: num(it.quantity),
      unit_price: num(it.mrp),
      price_ex_tax: num(it.unit_price_excl_tax ?? it.mrp),
      gst: num(it.gst_amount),
      gst_percent: num(it.tax_percentage),
      discount: num(it.discount_amount),
      total_price: num(it.total_amount ?? it.amount),
      products: [],
      addons: [],
      otherCharges: [],
    };
    parents.push(parent);
    parentByRef.set(key, parent);
  }

  for (const it of rawItems) {
    if (!it.is_child_row) continue;
    const parentKey = String(it.parent_ref_id ?? "");
    const parent = parentByRef.get(parentKey) ?? parents[parents.length - 1];
    if (!parent) continue;
    const kind = classifyChild(it);
    const child = {
      _id:
        it._id ??
        `${parent._key}-${kind}-${parent[kind === "product" ? "products" : kind === "addon" ? "addons" : "otherCharges"].length}`,
      quantity: num(it.quantity ?? 1),
      unit_price: num(it.mrp),
      price_ex_tax: num(it.unit_price_excl_tax ?? it.mrp),
      gst: num(it.gst_amount),
      gst_percent: num(it.tax_percentage),
      discount: num(it.discount_amount),
      total_price: num(it.total_amount ?? it.amount),
      hsn_code: it.hsn_code ?? "",
      expiry: it.expiry ?? "",
      batch_no: it.batch_number ?? it.batch_no ?? "",
    };
    if (kind === "product")
      parent.products.push({
        ...child,
        product_name: it.description ?? it.service_name ?? "Product",
      });
    else if (kind === "addon")
      parent.addons.push({
        ...child,
        name: it.description ?? it.service_name ?? "Addon",
      });
    else
      parent.otherCharges.push({
        ...child,
        description: it.description ?? it.service_name ?? "Other Charge",
        amount: num(it.mrp ?? it.total_amount),
      });
  }

  for (const it of rawItems) {
    if (it.is_child_row) continue;
    const key = String(it.ref_id ?? it._id ?? "");
    const parent = parentByRef.get(key);
    if (!parent) continue;
    for (const c of it.children ?? []) {
      const kind = classifyChild(c);
      const child = {
        _id:
          c._id ??
          `${parent._key}-child-${Math.random().toString(36).slice(2, 7)}`,
        quantity: num(c.quantity ?? 1),
        unit_price: num(c.mrp),
        price_ex_tax: num(c.unit_price_excl_tax ?? c.mrp),
        gst: num(c.gst_amount),
        gst_percent: num(c.tax_percentage),
        discount: num(c.discount_amount),
        total_price: num(c.total_amount ?? c.amount),
        hsn_code: c.hsn_code ?? "",
        expiry: c.expiry ?? "",
        batch_no: c.batch_number ?? c.batch_no ?? "",
      };
      if (kind === "product")
        parent.products.push({
          ...child,
          product_name: c.description ?? c.name ?? "Product",
        });
      else if (kind === "addon")
        parent.addons.push({
          ...child,
          name: c.description ?? c.name ?? "Addon",
        });
      else
        parent.otherCharges.push({
          ...child,
          description: c.description ?? c.name ?? "Other Charge",
          amount: num(c.mrp ?? c.total_amount),
        });
    }
  }

  const isProductInvoice =
    parents.length > 0 &&
    parents.every((p) => String(p.item_type || "").toLowerCase() === "product");
  const totalQty = parents.reduce((s, it) => {
    let q = it.quantity;
    it.products.forEach((p) => (q += p.quantity));
    it.addons.forEach((a) => (q += a.quantity));
    return s + q;
  }, 0);

  const payments = (snap.payments ?? []).map((p) => ({
    mode: p.payment_mode ?? "—",
    amount: num(p.paid_amount),
    date: p.payment_date ?? null,
    ref: p.reference_number ?? null,
  }));
  let paymentMode = "—";
  if (payments.length > 0) {
    const modes = [...new Set(payments.map((p) => p.mode).filter(Boolean))];
    if (modes.length) paymentMode = modes.join(", ");
  }

  const netAmount = num(snap.net_amount ?? summary.taxable_amount ?? 0);
  const totalDiscount = num(
    snap.discount_amount ?? summary.item_discount_amount ?? 0,
  );
  const subTotal = num(snap.sub_total ?? netAmount + totalDiscount);
  const roundOff = num(snap.round_off ?? summary.round_off ?? 0);
  const grandTotal = num(snap.grand_total ?? summary.invoice_total ?? 0);

  return {
    invoice_no: snap.invoice_no ?? "—",
    order_number: snap.invoice_no ?? snap.order_no ?? "—",
    order_no: snap.order_no ?? "—",
    createdAt:
      snap.invoice_date ?? snap.audit?.created_at ?? new Date().toISOString(),
    invoice_type: snap.invoice_type ?? "PROFORMA",
    payment_status: String(snap.payment_status ?? "unpaid").toLowerCase(),
    vendor: {
      name: vendor.name ?? vendor.brand_name ?? "—",
      phone: vendor.phone ?? "",
      email: vendor.email ?? "",
      gstNumber: vendor.gstin ?? "",
      addressLine: vendorAddress,
      logo: vendor.logo_url || vendor.logoDataUrl || vendor.logo || null,
      terms:
        vendor.terms ||
        "Goods once sold will not be taken back or exchanged. Subject to local jurisdiction.",
    },
    vendor_client: {
      phone: billTo.phone ?? "",
      email: billTo.email ?? "",
      name: billTo.name ?? "",
    },
    items: parents,
    is_product_invoice: isProductInvoice,
    total_qty: totalQty,
    sub_total: subTotal,
    net_amount: netAmount,
    total_discount: totalDiscount,
    total_gst: num(snap.tax_amount ?? summary.gst_amount ?? 0),
    round_off: roundOff,
    grand_total: grandTotal,
    balance_amount: num(invoiceAmount.balance_amount ?? snap.balance_due),
    payments,
    payment_mode: paymentMode,
    amount_in_words:
      snap.amount_in_words ||
      invoiceAmount.amount_in_words ||
      numberToWords(grandTotal),
  };
};

/* ================================================================
   PROFILE EXTRACTOR (Fixed to allow empty overrides)
   ================================================================ */
const extractProfile = (
  profileData,
  fallbackVendor = {},
  localOverrides = {},
) => {
  const d = profileData || {};
  const data = d.data || d;
  const v = d.vendor || data.vendor || {};
  const profile = data.profile || d.profile || {};
  const dataAddr = data.address || d.address || {};
  const photo = data.photo || d.photo || {};

  const backendOverrides =
    data.invoiceHeaderSettings || d.invoiceHeaderSettings || {};
  const overrides = { ...backendOverrides, ...localOverrides };

  // 🟢 Helper to strictly check if an override exists (allows empty string "")
  const getVal = (key, fallbacks) => {
    if (overrides[key] !== undefined && overrides[key] !== null) {
      return overrides[key];
    }
    for (const val of fallbacks) {
      if (val) return val;
    }
    return "";
  };

  const companyName = getVal("name", [
    v.businessName,
    v.brand_name,
    v.brand,
    v.companyName,
    v.name,
    profile.businessName,
    profile.brand,
    profile.companyName,
    profile.name,
    data.businessName,
    data.brand_name,
    data.companyName,
    data.name,
    fallbackVendor.businessName,
    fallbackVendor.brand_name,
    fallbackVendor.companyName,
    fallbackVendor.name,
  ]);

  const safeLogo = getVal("logo", [
    v.logo_url,
    v.logoDataUrl,
    v.logo,
    data.logo_url,
    data.logoDataUrl,
    data.logo,
    profile.logo_url,
    profile.logoDataUrl,
    profile.logo,
    profile.companyLogo,
    profile.companyLogoUrl,
    photo.profilePhoto,
    photo.logo,
    photo.companyLogo,
    Array.isArray(photo.shop) && photo.shop.length > 0 ? photo.shop[0] : "",
    fallbackVendor.logo_url,
    fallbackVendor.logoDataUrl,
    fallbackVendor.logo,
  ]);

  return {
    name: companyName,
    showSignature:
      overrides.showSignature !== undefined ? overrides.showSignature : true,
    phone: getVal("phone", [
      v.phone,
      profile.phone,
      data.phone,
      fallbackVendor.phone,
    ]),
    email: getVal("email", [
      v.email,
      profile.email,
      data.email,
      fallbackVendor.email,
    ]),
    gstin: getVal("gstin", [
      v.gstin,
      profile.gstin,
      data.gstin,
      d.gstin,
      fallbackVendor.gstin,
    ]),
    address: getVal("address", [
      formatAddress(v.address),
      formatAddress(dataAddr),
      formatAddress(fallbackVendor.address),
    ]),
    logo: safeLogo,
    terms: getVal("terms", [
      v.terms,
      profile.terms,
      "Goods once sold will not be taken back or exchanged. Subject to local jurisdiction.",
    ]),
  };
};

/* ================================================================
   EDITABLE FIELD + LOGO
   ================================================================ */
const PencilIcon = () => (
  <svg
    className="w-3.5 h-3.5"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
    />
  </svg>
);

const EditableField = ({
  value,
  onCommit,
  placeholder = "Click to edit",
  displayClassName = "",
  inputClassName = "",
  multiline = false,
  rows = 2,
  align = "left",
  isEditable = true,
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || "");

  useEffect(() => {
    setDraft(value || "");
  }, [value]);

  const commit = () => {
    setEditing(false);
    if (draft !== value) {
      onCommit(draft);
    }
  };
  const cancel = () => {
    setDraft(value || "");
    setEditing(false);
  };

  const justify =
    align === "right"
      ? "justify-end"
      : align === "center"
        ? "justify-center"
        : "justify-start";
  const textAlign =
    align === "right"
      ? "text-right"
      : align === "center"
        ? "text-center"
        : "text-left";

  const hasValue =
    value &&
    String(value).trim() !== "" &&
    value !== "—" &&
    value !== "null" &&
    value !== "[object Object]";

  if (!isEditable) {
    if (!hasValue) return null;
    return (
      <div className={`flex items-center gap-1.5 w-full ${justify}`}>
        <div className={`flex-1 ${textAlign} ${displayClassName}`}>
          <span className={multiline ? "whitespace-pre-wrap" : ""}>
            {value}
          </span>
        </div>
      </div>
    );
  }

  if (editing) {
    return (
      <div className={`no-print flex items-center gap-1.5 w-full ${justify}`}>
        <button
          type="button"
          onMouseDown={(e) => {
            e.preventDefault();
            cancel();
          }}
          className="text-xs bg-slate-200 text-slate-600 px-2.5 py-1.5 rounded hover:bg-slate-300 font-bold shrink-0"
          title="Cancel (Esc)"
        >
          X
        </button>
        {multiline ? (
          <textarea
            autoFocus
            rows={rows}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") cancel();
              if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) commit();
            }}
            className={`flex-1 bg-transparent border-b border-blue-500 focus:outline-none resize-none ${textAlign} ${inputClassName}`}
            placeholder={placeholder}
          />
        ) : (
          <input
            autoFocus
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") commit();
              if (e.key === "Escape") cancel();
            }}
            className={`flex-1 bg-transparent border-b border-blue-500 focus:outline-none ${textAlign} ${inputClassName}`}
            placeholder={placeholder}
          />
        )}
        <button
          type="button"
          onMouseDown={(e) => {
            e.preventDefault();
            commit();
          }}
          className="text-xs bg-blue-600 text-white px-3 py-1.5 rounded hover:bg-blue-700 font-medium shrink-0"
          title="Save (Enter)"
        >
          Save
        </button>
      </div>
    );
  }

  if (!hasValue) {
    return (
      <div className={`group flex items-center gap-1.5 w-full ${justify} h-5`}>
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="no-print opacity-0 group-hover:opacity-100 transition-opacity text-xs text-blue-500 hover:underline ml-auto"
        >
          + Add {placeholder}
        </button>
      </div>
    );
  }

  return (
    <div className={`group flex items-center gap-1.5 w-full ${justify}`}>
      <div className={`flex-1 ${textAlign} ${displayClassName}`}>
        <span className={multiline ? "whitespace-pre-wrap" : ""}>{value}</span>
      </div>
      <button
        type="button"
        onClick={() => setEditing(true)}
        className="no-print opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity text-gray-400 hover:text-blue-600 shrink-0 p-1 rounded"
        title={`Edit ${placeholder}`}
      >
        <PencilIcon />
      </button>
    </div>
  );
};

const EditableLogo = ({
  value,
  onCommit,
  height = "h-20",
  isEditable = true,
}) => {
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        onCommit(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleClear = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onCommit("");
  };

  const hasValue =
    value &&
    String(value).trim() !== "" &&
    value !== "null" &&
    value !== "[object Object]";

  if (!isEditable) {
    if (!hasValue) return null;
    return (
      <div className={`${height} w-auto flex items-center justify-start mb-4`}>
        <img
          src={value}
          alt="Logo"
          className={`${height} w-auto object-contain`}
        />
      </div>
    );
  }

  return (
    <>
      <div className="relative group mb-4 inline-block">
        {hasValue ? (
          <img
            src={value}
            alt="Logo"
            className={`${height} w-auto object-contain`}
          />
        ) : (
          <div
            className={`${height} w-20 bg-slate-100 items-center justify-center text-xl font-bold text-slate-400 rounded flex`}
          >
            LOGO
          </div>
        )}
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          accept="image/*"
          className="hidden"
        />
        {hasValue && (
          <button
            type="button"
            onMouseDown={(e) => {
              e.preventDefault();
              handleClear(e);
            }}
            className="no-print absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-[12px] shadow-sm hover:bg-red-600 z-10"
            title="Remove Logo"
          >
            ✕
          </button>
        )}
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className="no-print absolute -bottom-2 -right-2 bg-white border border-slate-200 p-1.5 rounded-full shadow-sm text-slate-500 hover:text-blue-600 hover:bg-slate-50 transition"
          title="Upload/Change Logo"
        >
          <PencilIcon />
        </button>
      </div>
    </>
  );
};

/* ================================================================
   PRINT STYLES
   ================================================================ */
const PrintStyles = () => (
  <style>{`
    @media print {
      @page { size: A4; margin: 10mm; }
      html, body { background: #fff !important; margin: 0 !important; padding: 0 !important; }
      body * { visibility: hidden !important; }
      #invoice-print-area, #invoice-print-area * { visibility: visible !important; }
      #invoice-print-area {
        position: absolute !important;
        left: 0; top: 0;
        width: 100% !important;
        border: none !important;
        box-shadow: none !important;
        padding: 0 !important;
      }
      .no-print { display: none !important; }
      input, textarea { border: none !important; background: transparent !important; box-shadow: none !important; outline: none !important; resize: none !important; appearance: none !important; }
      input::placeholder, textarea::placeholder { color: transparent !important; }
    }
  `}</style>
);

/* ================================================================
   MAIN COMPONENT
   ================================================================ */
export default function LetterheadFormat({
  demoData = null,
  hideToolbar = false,
  isEditable = false,
}) {
  const params = useParams();
  const [searchParams] = useSearchParams();
  const { profileData, fetchProfile, vendorId } = useProfile();

  const orderId =
    params.orderId ||
    searchParams.get("orderId") ||
    searchParams.get("id") ||
    "";

  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [profile, setProfile] = useState({
    name: "",
    phone: "",
    email: "",
    gstin: "",
    address: "",
    logo: "",
    terms: "",
    showSignature: true,
  });
  const [localOverrides, setLocalOverrides] = useState({});

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [saveSuccess, setSaveSuccess] = useState(false);

  const load = useCallback(async () => {
    if (demoData) {
      setInvoice(normalizeSnapshot(demoData));
      setLoading(false);
      return;
    }
    if (!orderId) {
      setError("No order id provided.");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await httpService.get(
        `/invoice/getInvoiceSnapshotByOrderId/${orderId}`,
      );
      const data = res?.data ?? res;
      setInvoice(normalizeSnapshot(data));
    } catch (e) {
      setError(
        e?.response?.data?.message ||
          e?.message ||
          "Failed to load invoice snapshot.",
      );
    } finally {
      setLoading(false);
    }
  }, [orderId, demoData]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const fallbackVendor = demoData?.vendor || {};
    const extracted = extractProfile(
      profileData,
      fallbackVendor,
      localOverrides,
    );
    setProfile(extracted);
  }, [profileData, demoData, localOverrides]);

  const handleInlineSave = async (field, value) => {
    const updatedProfile = { ...profile, [field]: value };
    setProfile(updatedProfile);

    const apiPayload = {
      name: updatedProfile.name,
      phone: updatedProfile.phone,
      email: updatedProfile.email,
      gstin: updatedProfile.gstin,
      address: updatedProfile.address,
      logo_url: updatedProfile.logo,
      terms: updatedProfile.terms,
      showSignature: updatedProfile.showSignature,
    };

    const overridePayload = {
      name: updatedProfile.name,
      phone: updatedProfile.phone,
      email: updatedProfile.email,
      gstin: updatedProfile.gstin,
      address: updatedProfile.address,
      logo: updatedProfile.logo,
      terms: updatedProfile.terms,
      showSignature: updatedProfile.showSignature,
    };

setLocalOverrides((prev) => ({ ...prev, ...overridePayload }));
setSaving(true);
setSaveError("");
setSaveSuccess(false);

const endpoint = vendorId
  ? `/vendorregistration/updateInvoiceHeaderSettings/${vendorId}`
  : `/vendorregistration/updateInvoiceHeaderSettings`;

try {
  await httpService.put(endpoint, apiPayload);

  // refresh is best-effort; do NOT fail the save if it throws
  if (typeof fetchProfile === "function") {
    try {
      await fetchProfile();
    } catch (_) {
      /* non-fatal */
    }
  }

  setSaveSuccess(true);
  setTimeout(() => setSaveSuccess(false), 2000);
} catch (e) {
  const msg = e?.response?.data?.message || e?.message || "Failed to save.";
  setSaveError(msg);
  console.error("[invoice header save failed]", e);

  // revert both the visible profile AND the local override
  setProfile(profile);
  setLocalOverrides((prev) => {
    const next = { ...prev };
    delete next[field];
    return next;
  });
} finally {
  setSaving(false);
}
  };

  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 text-gray-600 font-sans">
        Loading invoice…
      </div>
    );
  if (error)
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-100 gap-3">
        <p className="text-red-600 font-sans">{error}</p>
        <button
          onClick={load}
          className="px-4 py-2 bg-black text-white rounded text-sm"
        >
          Retry
        </button>
      </div>
    );
  if (!invoice) return null;

  const customerName = formatAddress(invoice.vendor_client.name);
  const showExpiry = invoice.is_product_invoice;
  const orderNo =
    invoice.order_no || (invoice.order_number || "").replace("INV", "ORD");

  return (
    <div className="min-h-screen bg-slate-100 font-sans text-slate-800">
      <PrintStyles />
      {saveError && (
        <div className="no-print fixed top-4 right-4 z-50 bg-red-600 text-white px-4 py-2 rounded shadow-lg text-sm max-w-md">
          {saveError}
        </div>
      )}
      {saveSuccess && (
        <div className="no-print fixed top-4 right-4 z-50 bg-emerald-600 text-white px-4 py-2 rounded shadow-lg text-sm">
          Saved ✓
        </div>
      )}

      {!hideToolbar && (
        <div className="no-print sticky top-0 z-20 bg-white border-b border-slate-200 shadow-sm">
          <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
            <div>
              <h1 className="text-sm font-semibold text-slate-800">
                Invoice Preview
              </h1>
              <p className="text-xs text-slate-500">
                {invoice.invoice_no || invoice.order_number}
              </p>
            </div>
            <button
              onClick={() => window.print()}
              className="px-4 py-1.5 text-sm rounded bg-slate-900 text-white hover:bg-slate-800 shadow-sm transition"
            >
              🖨 Print Invoice
            </button>
          </div>
        </div>
      )}

      <div className="flex justify-center p-4 sm:p-8">
        <div
          id="invoice-print-area"
          className="bg-white w-full max-w-[210mm] mx-auto p-10 shadow-xl border border-slate-200 min-h-[297mm] flex flex-col"
        >
          {/* ================= HEADER ================= */}
          <div className="flex justify-between items-start border-b-[3px] border-slate-800 pb-6 mb-6">
            <div className="relative z-10 flex flex-col items-start w-1/2">
              <EditableLogo
                value={profile.logo}
                onCommit={(val) => handleInlineSave("logo", val)}
                height="h-20"
                isEditable={isEditable}
              />
              <EditableField
                value={profile.name}
                onCommit={(val) => handleInlineSave("name", val)}
                placeholder="Business Name"
                displayClassName="text-3xl font-extrabold text-slate-900 uppercase tracking-tight leading-tight"
                inputClassName="text-2xl font-bold text-slate-900 uppercase"
                isEditable={isEditable}
              />
            </div>

            <div className="w-1/2 flex flex-col items-end text-right">
              <h2 className="text-lg font-bold text-slate-300 uppercase tracking-widest mb-4">
                {invoice.invoice_type === "TAX" ? "TAX INVOICE" : "INVOICE"}
              </h2>

              <div className="text-sm text-slate-600 flex flex-col items-end gap-1 w-full max-w-[300px]">
                <EditableField
                  value={profile.address}
                  onCommit={(val) => handleInlineSave("address", val)}
                  placeholder="Company Address"
                  multiline
                  rows={2}
                  align="right"
                  displayClassName="text-sm text-slate-600"
                  inputClassName="text-sm text-slate-600"
                  isEditable={isEditable}
                />
                <EditableField
                  value={profile.phone}
                  onCommit={(val) => handleInlineSave("phone", val)}
                  placeholder="Phone Number"
                  align="right"
                  displayClassName="font-bold text-slate-900 text-base"
                  inputClassName="font-bold text-slate-900 text-base"
                  isEditable={isEditable}
                />
                <EditableField
                  value={profile.email}
                  onCommit={(val) => handleInlineSave("email", val)}
                  placeholder="Email Address"
                  align="right"
                  displayClassName="text-sm text-slate-600"
                  inputClassName="text-sm text-slate-600"
                  isEditable={isEditable}
                />
                <div className="inline-block mt-1">
                  <EditableField
                    value={profile.gstin}
                    onCommit={(val) => handleInlineSave("gstin", val)}
                    placeholder="GSTIN"
                    align="right"
                    displayClassName="font-semibold text-slate-800 text-sm bg-slate-100 px-2 py-0.5 rounded"
                    inputClassName="font-semibold text-slate-800 text-sm"
                    isEditable={isEditable}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* ================= BILL TO & INVOICE META ================= */}
          <div className="flex justify-between items-start mb-8">
            <div className="w-1/2">
              <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">
                Bill To:
              </h3>
              <p className="font-bold text-lg text-slate-900">
                {formatAddress(customerName) || "Walk-in Customer"}
              </p>
              {invoice.vendor_client.phone && (
                <p className="text-sm text-slate-600 mt-0.5">
                  Ph: {invoice.vendor_client.phone}
                </p>
              )}
              {invoice.vendor_client.email && (
                <p className="text-sm text-slate-600">
                  {invoice.vendor_client.email}
                </p>
              )}
            </div>

            <div className="w-1/2 flex justify-end">
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 min-w-[240px] text-sm text-slate-700 space-y-1.5 shadow-sm">
                <div className="flex justify-between gap-6">
                  <span className="font-semibold text-slate-500">
                    Invoice No:
                  </span>
                  <span className="font-bold text-slate-900">
                    {invoice.invoice_no}
                  </span>
                </div>
                <div className="flex justify-between gap-6">
                  <span className="font-semibold text-slate-500">
                    Order No:
                  </span>
                  <span>{orderNo}</span>
                </div>
                <div className="flex justify-between gap-6">
                  <span className="font-semibold text-slate-500">Date:</span>
                  <span>{fmtDate(invoice.createdAt)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* ================= ITEMS TABLE ================= */}
          <table className="w-full text-left mb-8 border-collapse text-sm">
            <thead>
              <tr className="border-b-2 border-slate-800 text-[11px] text-slate-800 uppercase tracking-wider bg-slate-50">
                <th
                  className={`py-3 px-2 font-bold leading-tight ${showExpiry ? "w-[23%]" : "w-[35%]"}`}
                >
                  Item Name
                  <br />
                  <span className="text-[10px] font-normal text-slate-500 normal-case">
                    HSN / SAC
                  </span>
                </th>
                {showExpiry && (
                  <th className="py-3 px-2 font-bold w-[12%] leading-tight">
                    Expiry
                    <br />
                    <span className="text-[10px] font-normal text-slate-500 normal-case">
                      Batch No
                    </span>
                  </th>
                )}
                <th className="py-3 px-2 font-bold text-right w-[6%]">Qty</th>
                <th className="py-3 px-2 font-bold text-right w-[12%]">MRP</th>
                <th className="py-3 px-2 font-bold text-right w-[13%] leading-tight">
                  Disc Amt
                  <br />
                  <span className="text-[10px] font-normal text-slate-500 normal-case">
                    Disc %
                  </span>
                </th>
                <th className="py-3 px-2 font-bold text-right w-[12%] leading-tight">
                  Tax Amt
                  <br />
                  <span className="text-[10px] font-normal text-slate-500 normal-case">
                    Tax %
                  </span>
                </th>
                <th className="py-3 px-2 font-bold text-right w-[22%]">
                  Total
                </th>
              </tr>
            </thead>
            <tbody>
              {invoice.items.map((item, i) => (
                <React.Fragment key={item._id || i}>
                  <tr className="border-b border-slate-200 hover:bg-slate-50/50 transition-colors">
                    <td className="py-3 px-2 align-top">
                      <div
                        className="font-bold text-slate-900 truncate"
                        title={item.service_name}
                      >
                        {truncate(item.service_name, 25)}
                      </div>
                      <div
                        className="text-[11px] text-slate-500 truncate"
                        title={item.hsn_code}
                      >
                        {item.hsn_code || "-"}
                      </div>
                    </td>
                    {showExpiry && (
                      <td className="py-3 px-2 align-top">
                        <div
                          className="text-[12px] text-slate-700 truncate"
                          title={item.expiry}
                        >
                          {item.expiry || "-"}
                        </div>
                        <div
                          className="text-[11px] text-slate-500 truncate"
                          title={item.batch_no}
                        >
                          {item.batch_no || "-"}
                        </div>
                      </td>
                    )}
                    <td className="py-3 px-2 text-right align-top font-bold">
                      {item.quantity}
                    </td>
                    <td className="py-3 px-2 text-right align-top font-bold">
                      {fmtMoney(item.unit_price)}
                    </td>
                    <td className="py-3 px-2 text-right align-top font-bold">
                      {(() => {
                        const gross = item.unit_price * item.quantity;
                        const eff = item.discount;
                        const pct = gross > 0 ? (eff / gross) * 100 : 0;
                        return (
                          <>
                            <div>{eff > 0 ? `-${fmtMoney(eff)}` : "-"}</div>
                            <div className="text-[11px] text-slate-500">
                              {pct > 0 ? `${Math.round(pct)}%` : "-"}
                            </div>
                          </>
                        );
                      })()}
                    </td>
                    <td className="py-3 px-2 text-right align-top font-bold">
                      <div>{fmtMoney(item.gst)}</div>
                      <div className="text-[11px] text-slate-500">
                        {Math.round(item.gst_percent)}%
                      </div>
                    </td>
                    <td className="py-3 px-2 text-right align-top font-bold text-slate-900">
                      {fmtMoney(item.total_price)}
                    </td>
                  </tr>

                  {item.products?.map((p, pi) => {
                    const gross = p.unit_price * p.quantity;
                    const eff = p.discount;
                    const pct = gross > 0 ? (eff / gross) * 100 : 0;
                    return (
                      <tr
                        key={p._id || pi}
                        className="border-b border-slate-100 text-xs text-slate-600 bg-slate-50/40"
                      >
                        <td className="py-2 px-2 pl-6 align-top">
                          <div
                            className="truncate font-medium"
                            title={p.product_name}
                          >
                            └ {truncate(p.product_name, 23)}
                          </div>
                          <div
                            className="text-[10px] text-slate-400 truncate"
                            title={p.hsn_code}
                          >
                            {p.hsn_code || "-"}
                          </div>
                        </td>
                        {showExpiry && (
                          <td className="py-2 px-2 align-top">
                            <div className="text-[11px] text-slate-500 truncate">
                              {p.expiry || "-"}
                            </div>
                            <div className="text-[10px] text-slate-400 truncate">
                              {p.batch_no || "-"}
                            </div>
                          </td>
                        )}
                        <td className="py-2 px-2 text-right align-top font-bold">
                          {p.quantity}
                        </td>
                        <td className="py-2 px-2 text-right align-top font-bold">
                          {fmtMoney(p.unit_price)}
                        </td>
                        <td className="py-2 px-2 text-right align-top font-bold">
                          <div>{eff > 0 ? `-${fmtMoney(eff)}` : "-"}</div>
                          <div className="text-[10px] text-slate-500">
                            {pct > 0 ? `${Math.round(pct)}%` : "-"}
                          </div>
                        </td>
                        <td className="py-2 px-2 text-right align-top font-bold">
                          <div>{fmtMoney(p.gst)}</div>
                          <div className="text-[10px] text-slate-500">
                            {Math.round(p.gst_percent)}%
                          </div>
                        </td>
                        <td className="py-2 px-2 text-right align-top font-medium">
                          {fmtMoney(p.total_price)}
                        </td>
                      </tr>
                    );
                  })}

                  {item.addons?.map((a, ai) => {
                    const qty = a.quantity || 1;
                    const gross = a.unit_price * qty;
                    const eff = a.discount;
                    const pct = gross > 0 ? (eff / gross) * 100 : 0;
                    return (
                      <tr
                        key={a._id || ai}
                        className="border-b border-slate-100 text-xs text-slate-600 bg-slate-50/40"
                      >
                        <td className="py-2 px-2 pl-6 align-top">
                          <div className="truncate font-medium" title={a.name}>
                            └ {truncate(a.name, 23)}
                          </div>
                          <div className="text-[10px] text-slate-400 truncate">
                            {a.hsn_code || "-"}
                          </div>
                        </td>
                        {showExpiry && (
                          <td className="py-2 px-2 align-top">
                            <div className="text-[11px] text-slate-500 truncate">
                              {a.expiry || "-"}
                            </div>
                            <div className="text-[10px] text-slate-400 truncate">
                              {a.batch_no || "-"}
                            </div>
                          </td>
                        )}
                        <td className="py-2 px-2 text-right align-top font-bold">
                          {qty}
                        </td>
                        <td className="py-2 px-2 text-right align-top font-bold">
                          {fmtMoney(a.unit_price)}
                        </td>
                        <td className="py-2 px-2 text-right align-top font-bold">
                          <div>{eff > 0 ? `-${fmtMoney(eff)}` : "-"}</div>
                          <div className="text-[10px] text-slate-500">
                            {pct > 0 ? `${Math.round(pct)}%` : "-"}
                          </div>
                        </td>
                        <td className="py-2 px-2 text-right align-top font-bold">
                          <div>{fmtMoney(a.gst)}</div>
                          <div className="text-[10px] text-slate-500">
                            {Math.round(a.gst_percent)}%
                          </div>
                        </td>
                        <td className="py-2 px-2 text-right align-top font-medium">
                          {fmtMoney(a.total_price)}
                        </td>
                      </tr>
                    );
                  })}

                  {item.otherCharges?.map((oc, oi) => (
                    <tr
                      key={oc._id || oi}
                      className="border-b border-slate-100 text-xs text-slate-600 bg-slate-50/40"
                    >
                      <td
                        className="py-2 px-2 pl-6 align-top truncate font-medium"
                        title={oc.description}
                      >
                        └ {truncate(oc.description, 23)}
                      </td>
                      {showExpiry && (
                        <td className="py-2 px-2 align-top text-slate-400">
                          —
                        </td>
                      )}
                      <td className="py-2 px-2 text-right align-top font-bold">
                        1
                      </td>
                      <td className="py-2 px-2 text-right align-top font-bold">
                        {fmtMoney(oc.amount)}
                      </td>
                      <td className="py-2 px-2 text-right align-top font-bold">
                        —
                      </td>
                      <td className="py-2 px-2 text-right align-top font-bold">
                        —
                      </td>
                      <td className="py-2 px-2 text-right align-top font-medium">
                        {fmtMoney(oc.total_price)}
                      </td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>

          {/* ================= TOTALS ================= */}
          <div className="flex justify-end mb-8">
            <div className="w-full md:w-1/2 lg:w-[45%] bg-slate-50/50 p-4 rounded-lg border border-slate-200">
              {invoice.total_discount > 0 && (
                <>
                  <div className="flex justify-between py-1.5 text-sm text-slate-600">
                    <span>Sub Total:</span>
                    <span className="font-medium text-slate-900">
                      {fmtMoney(invoice.sub_total)}
                    </span>
                  </div>
                  <div className="flex justify-between py-1.5 text-sm text-emerald-600">
                    <span>Item Discount:</span>
                    <span>- {fmtMoney(invoice.total_discount)}</span>
                  </div>
                </>
              )}

              <div className="flex justify-between py-1.5 text-sm">
                <span className="text-slate-600">Net Amount:</span>
                <span className="font-medium text-slate-900">
                  {fmtMoney(invoice.net_amount)}
                </span>
              </div>
              <div className="flex justify-between py-1.5 text-sm">
                <span className="text-slate-600">Tax:</span>
                <span className="font-medium text-slate-900">
                  {fmtMoney(invoice.total_gst)}
                </span>
              </div>

              {invoice.round_off !== 0 && (
                <div className="flex justify-between py-1.5 text-sm text-slate-600">
                  <span>Round Off:</span>
                  <span>
                    {invoice.round_off > 0 ? "+" : "-"}
                    {fmtMoney(Math.abs(invoice.round_off))}
                  </span>
                </div>
              )}

              <div className="flex justify-between py-2.5 border-t-2 border-slate-800 mt-2">
                <span className="font-bold text-lg text-slate-900">
                  Order Amount:
                </span>
                <span>₹{Math.round(Number(invoice.grand_total))}</span>
              </div>

              {invoice.balance_amount > 0 && (
                <div className="flex justify-between py-1 text-sm text-rose-600 font-bold border-t border-rose-200 mt-1 pt-1">
                  <span>Balance Due:</span>
                  <span>{fmtMoney(invoice.balance_amount)}</span>
                </div>
              )}
            </div>
          </div>

          {/* ================= FOOTER ================= */}
          <div className="border-t border-slate-300 pt-4 mt-auto">
            <div className="mb-6">
              <p className="text-sm">
                <span className="font-bold text-slate-800">
                  Amount in words:{" "}
                </span>
                <span className="text-slate-600 italic">
                  {invoice.amount_in_words}
                </span>
              </p>
              {invoice.payment_mode && invoice.payment_mode !== "—" && (
                <p className="text-sm mt-1">
                  <span className="font-bold text-slate-800">
                    Payment Mode:{" "}
                  </span>
                  <span className="uppercase text-slate-600">
                    {invoice.payment_mode}
                  </span>
                </p>
              )}
            </div>

            <div className="flex justify-between items-end mt-12 pt-8">
              <div className="text-xs text-slate-500 max-w-sm w-full">
                <p className="font-bold text-slate-600 mb-1">
                  Terms & Conditions:
                </p>
                <EditableField
                  value={profile.terms}
                  onCommit={(val) => handleInlineSave("terms", val)}
                  placeholder="Terms and Conditions"
                  multiline
                  rows={3}
                  displayClassName="text-xs text-slate-500 leading-snug"
                  inputClassName="text-xs text-slate-500"
                  isEditable={isEditable}
                />
              </div>

              <div className="text-center w-48 flex flex-col items-center relative group">
                {profile.showSignature !== false ? (
                  <div className="relative w-full">
                    {isEditable && (
                      <button
                        type="button"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          handleInlineSave("showSignature", false);
                        }}
                        className="no-print absolute -top-6 -right-2 text-red-500 hover:text-red-700 opacity-0 group-hover:opacity-100 transition-opacity bg-white rounded-full p-1 shadow-sm border border-gray-200"
                        title="Remove Signature"
                      >
                        <svg
                          className="w-3.5 h-3.5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M6 18L18 6M6 6l12 12"
                          ></path>
                        </svg>
                      </button>
                    )}

                    <div className="h-16" />
                    <div className="border-t border-slate-400 w-full pt-1 text-xs text-slate-500">
                      Authorised Signatory
                    </div>
                  </div>
                ) : (
                  isEditable && (
                    <button
                      type="button"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        handleInlineSave("showSignature", true);
                      }}
                      className="no-print text-xs text-blue-500 hover:underline bg-slate-50 p-2 rounded border border-slate-200 h-10"
                    >
                      + Add Signature
                    </button>
                  )
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
